<script>
import { ref, onMounted, computed } from '@vue/composition-api'
import moment from 'moment'
import { useToast } from 'vue-toastification/composition'

import { useCollections, useMetrics } from '@/features'
import store from '@/store'
import { formatPercent } from '@/helpers'
import { NebActionButton } from '@/components/Nebula/controls'
import router from '@/router'
import DeleteConfirmModal from '@/components/Collections/DeleteConfirmModal.vue'
import VueElementLoading from 'vue-element-loading'

const actionOptions = [
  { label: 'View', value: 'view' },
  { label: 'Copy', value: 'copy' },
  { label: 'Delete', value: 'delete' }
]

export default {
  name: 'Collections',

  components: {
    NebActionButton,
    DeleteConfirmModal,
    VueElementLoading
  },

  setup () {
    // Local State
    const brandId = store.state.brand.settings.id
    const showDeleteModal = ref(false)
    const toBeDeletedCollection = ref(null)

    // Features
    const toast = useToast()
    const collections = useCollections()
    const metrics = useMetrics({
      breakdown: 'collection',
      brandIds: [brandId],
      metrics: [
        { metric: 'page_views', agg: 'sum' },
        { metric: 'asset_count', agg: 'sum' },
        { metric: 'social_actions', agg: 'sum' },
        { metric: 'completion_rate', agg: 'avg' },
        { metric: 'conversion_rate', agg: 'avg' }
      ]
    })

    // Computed State
    const hydratedCollections = computed(() =>
      collections.list.value
        .map(c => {
          const metricObj = metrics.data.value.find(o => o.COLLECTION === c.name)
          return Object.assign({}, c, { metrics: metricObj || {}})
        })
    )

    // UI Actions
    const onDeleteModalClose = () => {
      showDeleteModal.value = false
      toBeDeletedCollection.value = null
    }

    // Collections Actions
    const copyCollection = async collection => {
      const { name } = collection
      let newName = `${name} (1)`

      if (name.match(/\((\d+)\)$/)) {
        const number = parseInt(name.match(/\((\d+)\)$/)[1]) + 1
        const version = ` (${number})`

        newName = name.substr(0, name.length - name.match(/\((\d+)\)$/).length - 2) + version
      }

      await collections.create(newName, collection.description, collection.brand_id, collection.asset_set)
    }
    const handleDelete = async () => {
      showDeleteModal.value = false
      await collections.performDelete(toBeDeletedCollection.value.id)
      toBeDeletedCollection.value = null
      await init()
      toast.success('Collection Deleted')
    }
    const onActionSelect = async (option, collection) => {
      switch (option.value) {
        case 'view':
          router.push({
            name: 'organize',
            query: { collection: String(collection.id) }
          })
          break
        case 'copy':
          await copyCollection(collection)
          toast.success('Collection copied')
          await init()
          break
        case 'delete':
          toBeDeletedCollection.value = collection
          showDeleteModal.value = true
          break
        default: return
      }
    }

    // Lifecycle
    const init = async () => {
      await collections.getList(brandId)
      await metrics.loadMetrics()
    }
    onMounted(async () => {
      await init()
    })

    return {
      collections: collections.list,
      hydratedCollections,
      formatPercent,
      moment,
      actionOptions,
      onActionSelect,
      showDeleteModal,
      toBeDeletedCollection,
      onDeleteModalClose,
      handleDelete,
      loading: computed(() => collections.loading.value || metrics.dataLoading.value)
    }
  }
}
</script>

<template>
  <div id="collectionsView">
    <div class="container">
      <div class="titleWrapper">
        <h1>Collections</h1>
      </div>
      <div class="infoText">
        <p>To create a new Collection go to the Organize or Measure tab, filter assets, and then click the “Action” button. To view a collection you created, go to the Organize or Measure tab and choose the collection from the collection filter.</p>
      </div>
    </div>
    <div class="container">
      <vue-element-loading :active="loading" />
      <div class="headerRow">
        <div class="headerRow-cell"><p>Name</p></div>
        <div class="headerRow-cell"><p>Asset Count</p></div>
        <div class="headerRow-cell"><p>Views</p></div>
        <div class="headerRow-cell"><p>Social Actions</p></div>
        <div class="headerRow-cell"><p>Avg. Completion Rate</p></div>
        <div class="headerRow-cell"><p>Avg. Conversion Rate</p></div>
        <div class="headerRow-cell"><p>Last Updated</p></div>
        <div class="headerRow-cell"></div>
      </div>
      <div class="tableContent">
        <div class="collectionRow" v-for="collection in hydratedCollections" :key="collection.id">
          <div class="collectionRow-cell name">
            <p>{{collection.name}}</p>
          </div>
          <div class="collectionRow-cell assetCount">
            <p class="assetCount">{{collection.metrics.SUM__ASSET_COUNT || '--'}}</p>
          </div>
          <div class="collectionRow-cell">
            <p>{{collection.metrics.SUM__PAGE_VIEWS || '--'}}</p>
          </div>
          <div class="collectionRow-cell">
            <p>{{collection.metrics.SUM__SOCIAL_ACTIONS || '--'}}</p>
          </div>
          <div class="collectionRow-cell">
            <p>{{formatPercent(collection.metrics.AVG__COMPLETION_RATE)}}</p>
          </div>
          <div class="collectionRow-cell">
            <p>{{formatPercent(collection.metrics.AVG__CONVERSION_RATE)}}</p>
          </div>
          <div class="collectionRow-cell">
            <p>{{moment(collection.last_updated).format('MM/DD/YYYY')}}</p>
          </div>
          <div class="collectionRow-cell">
            <neb-action-button text="Actions"
              :dropdownOptions="actionOptions"
              @select="opt => onActionSelect(opt, collection)"
            />
          </div>
        </div>
      </div>
    </div>
    <delete-confirm-modal :isOpen="showDeleteModal"
      :collectionName="toBeDeletedCollection ? toBeDeletedCollection.name : 'Unknown'"
      @action:close="onDeleteModalClose"
      @action:delete="handleDelete"
    />
  </div>
</template>

<style lang="scss" scoped>
@import '../../components/Nebula/style.scss';

#collectionsView {
  font-family: $defaultFont;
  padding-bottom: 5em;

  p {
    margin: 0;
  }
  .titleWrapper {
    border-bottom: 1px solid $pinkish-grey;
    padding: 1.5em 0;

    h1 {
      font-size: 20px;
      margin: 0;
    }
  }

  .infoText {
    padding: 1em 0;
  }
  .container {
    width: 1167px;
    margin: auto;
  }
  .tableContent {
    border-bottom: 4px solid $slate-grey;
  }
  .headerRow {
    display: flex;
    justify-content: space-between;
    border-bottom: 2px solid $slate-grey;
    border-top: 4px solid $slate-grey;

    &-cell {
      flex-basis: 10%;
      padding: 1.5em 0.5em;
      text-align: center;

      p {
        font-weight: bold;
        font-size: 12px;
      }
    }
  }
  .collectionRow {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &-cell {
      flex-basis: 10%;
      padding: 1em 0.5em;
      text-align: center;

      &.name {
        text-align: right;
      }

      &.assetCount {
        p {
          text-decoration: underline;
        }
      }
    }

    &:hover {
      background-color: $ice-blue;
    }
  }
}
</style>
